/**
 * 組織レポートのダミーデータ (gb-org-questionnaire-74)
 *
 * docs/mockups/13_モック_GB組織_レポート_v4.html の数値を再現する。
 * 実データ接続時は集計API (/api/analytics/aggregation/company/:id) と
 * orgScore.ts の集約ロジックに差し替える。
 */
import type { AxisKey } from './gb74Report';
import type { DeptConnectionStructure } from './deptConnectionStructure';
import type { ConsultNetwork } from './dummyConsultNetwork';
import type { HerrmannLoadDisplay } from './herrmannLoad';

export interface OrgGauge {
  label: string;
  score: number; // 0〜100
  grade: 'A' | 'B' | 'C' | 'D';
  emphasis?: boolean;
}

export interface ScatterDot {
  dept: string;
  /** @deprecated 部署色分けは廃止。ミニ・ダイヤ形で描画する */
  color?: string;
  /** 0〜1 の正規化座標。左上=A論理 右上=D創造 右下=C共感 左下=B着実 */
  x: number;
  y: number;
  /** 各個人の4軸スコア(0〜12)。散布図でミニ・ダイヤ形を描くのに使う。未指定時は座標から近似 */
  axis?: Record<AxisKey, number>;
}

export interface ImprovementItem {
  rank: number;
  rankColor: string;
  title: string;
  reason: string;
  action: string;
  effect: string;
}

export interface DeptHeatRow {
  dept: string;
  cells: Array<{ label: string; grade: 'A' | 'B' | 'C' | 'D' }>;
}

export interface OrgReportData {
  companyName: string;
  meta: string;
  /** 総合評価 0〜100 */
  totalScore: number;
  totalGrade: 'A' | 'B' | 'C' | 'D';
  evaluationLead: string;
  /** 組織ダイヤモンド 4軸 (thinkingType組織平均、0〜12スケール) */
  axis: Record<AxisKey, number>;
  axisNote: string;
  gauges: OrgGauge[];
  /**
   * §01 ハーマン・コミュニケーション負荷のつながりスコア反映 (設計ドラフト・レベル3仮説)。
   * 指定時のみ §01 に負荷パネルを表示。つながりスコアは補正後の値を gauges に反映済み。
   */
  herrmannLoad?: HerrmannLoadDisplay;
  distributionLead: string;
  scatter: ScatterDot[];
  deptLegend: Array<{ dept: string; color: string }>;
  connectionLead: string;
  /** §03 部署レベルのつながり構造 (トリニティ/V字。レベルA算出結果。任意) */
  connectionStructure?: DeptConnectionStructure;
  /**
   * §03 レベルB 個人ネットワーク (相談エッジ→キーパーソン/V字)。指定時は§03を
   * 部門ピル一覧＋クリック展開のネットワーク図で表示する。実名は表示しない(匿名ラベル)。
   */
  consultNetwork?: ConsultNetwork;
  improvementLead: string;
  improvementTarget: number;
  improvements: ImprovementItem[];
  /** GB組織タイプ12分類の判定結果（orgArchetypes.ts）。未判定時は undefined */
  orgArchetypeId?: string;
  /** 原型名（例: 協働・良好） */
  orgArchetypeName?: string;
  /** 一言キャラクター（例: 人の繋がりが強み） */
  orgArchetypeCharacter?: string;
  closingText: string;
  heatHeader: string[];
  heatRows: DeptHeatRow[];
}

const C = { mfg: '#3B82F6', sales: '#EC4899', admin: '#8B5CF6' };

/** モック13 (株式会社丸山製作所) を再現したダミーデータ */
export const DUMMY_ORG_REPORT: OrgReportData = {
  companyName: '株式会社丸山製作所',
  meta: '2026年4月実施｜従業員40名｜製造業｜回答率 88%',
  totalScore: 52,
  totalGrade: 'C',
  evaluationLead:
    '一部に注意が必要な状態です。特に「つながり」の改善が、全体を良くする最短の道筋です。',
  // 安定寄り・変化やや弱め (B=安定 を高め、D=変化 を低め)
  axis: { A: 6.5, B: 8.0, C: 7.0, D: 4.5 },
  axisNote: '安定寄り。変化の力がやや弱め。',
  gauges: [
    { label: '働きがい', score: 68, grade: 'B' },
    { label: 'つながり', score: 42, grade: 'C', emphasis: true },
    { label: '離職の兆し', score: 45, grade: 'C' },
  ],
  distributionLead:
    '管理部が堅実タイプに集中、営業部が推進タイプに集中しています。製造部は中央に散らばり、バランスの良い構成です。',
  scatter: [
    // 製造部 (中央)
    { dept: '製造部', color: C.mfg, x: 0.38, y: 0.3 },
    { dept: '製造部', color: C.mfg, x: 0.45, y: 0.42 },
    { dept: '製造部', color: C.mfg, x: 0.5, y: 0.48 },
    { dept: '製造部', color: C.mfg, x: 0.55, y: 0.35 },
    { dept: '製造部', color: C.mfg, x: 0.42, y: 0.55 },
    { dept: '製造部', color: C.mfg, x: 0.48, y: 0.4 },
    { dept: '製造部', color: C.mfg, x: 0.6, y: 0.32 },
    { dept: '製造部', color: C.mfg, x: 0.4, y: 0.58 },
    { dept: '製造部', color: C.mfg, x: 0.52, y: 0.44 },
    { dept: '製造部', color: C.mfg, x: 0.46, y: 0.5 },
    // 営業部 (右下=推進)
    { dept: '営業部', color: C.sales, x: 0.72, y: 0.62 },
    { dept: '営業部', color: C.sales, x: 0.68, y: 0.68 },
    { dept: '営業部', color: C.sales, x: 0.75, y: 0.72 },
    { dept: '営業部', color: C.sales, x: 0.65, y: 0.58 },
    { dept: '営業部', color: C.sales, x: 0.78, y: 0.65 },
    { dept: '営業部', color: C.sales, x: 0.62, y: 0.7 },
    // 管理部 (左上=堅実)
    { dept: '管理部', color: C.admin, x: 0.28, y: 0.25 },
    { dept: '管理部', color: C.admin, x: 0.22, y: 0.3 },
    { dept: '管理部', color: C.admin, x: 0.32, y: 0.22 },
    { dept: '管理部', color: C.admin, x: 0.25, y: 0.28 },
    { dept: '管理部', color: C.admin, x: 0.3, y: 0.35 },
    { dept: '管理部', color: C.admin, x: 0.28, y: 0.32 },
  ],
  deptLegend: [
    { dept: '製造部', color: C.mfg },
    { dept: '営業部', color: C.sales },
    { dept: '管理部', color: C.admin },
  ],
  connectionLead:
    '△が多い＝誰かが抜けてもカバーできる。∨が多い＝1人に集中。製造部で田中さんへのV字集中が最大の課題です。',
  improvementLead:
    '今の総合評価はC（52点）。以下の3つに取り組むと、B（60点以上）に改善できる見込みがあります。',
  improvementTarget: 63,
  improvements: [
    {
      rank: 1,
      rankColor: '#B33A3A',
      title: '製造部の「V字」を「三角形」に変える',
      reason:
        '田中さんへの相談集中（72%）を分散させる。仕事のやり方をもう1人に共有し、3人で相談し合える三角形を3つ作る。',
      action: '田中さんの仕事のやり方を、メモでいいので1つだけ書き出してもらう',
      effect: 'つながり 42→55（+13点）見込み',
    },
    {
      rank: 2,
      rankColor: '#B8860B',
      title: '営業部に月1回の育成の場を作る',
      reason:
        '入社2〜3年目の「成長実感」が低い。月1回30分の「学びの場」で先輩の仕事のコツを共有する。',
      action: '入社2〜3年目の社員に「最近、新しく学んだことある？」と聞いてみる',
      effect: '離職の兆し 45→55（+10点）見込み',
    },
    {
      rank: 3,
      rankColor: '#999',
      title: '管理部の承認を1つ部下に任せる',
      reason:
        '社長に判断が集中するV字構造。1つ委譲するだけで三角形が生まれ、社員の自律性が上がる。',
      action: '管理部の承認の流れを1つだけ、部下に任せてみる',
      effect: '働きがい 68→72（+4点）＋つながりに波及',
    },
  ],
  closingText:
    '今回の診断で見えたことがあります。\n御社には「助け合える」「話しやすい」という強い土台があります。\nその一方で、特定の方に頼りすぎているV字の構造が弱点になっています。\n\nV字を三角形に変えていくこと——\n三角形が1つ増えるだけで、組織はぐっと安定します。\n\nまずは今月の取り組みを1つだけ。\n小さな一歩が、組織を変える最初の動きになります。',
  heatHeader: ['働きがい', '助け合い', '頼りすぎ', '離職の兆し'],
  heatRows: [
    {
      dept: '製造部',
      cells: [
        { label: '働きがい', grade: 'B' },
        { label: '助け合い', grade: 'A' },
        { label: '頼りすぎ', grade: 'D' },
        { label: '離職の兆し', grade: 'B' },
      ],
    },
    {
      dept: '営業部',
      cells: [
        { label: '働きがい', grade: 'B' },
        { label: '助け合い', grade: 'B' },
        { label: '頼りすぎ', grade: 'B' },
        { label: '離職の兆し', grade: 'C' },
      ],
    },
    {
      dept: '管理部',
      cells: [
        { label: '働きがい', grade: 'A' },
        { label: '助け合い', grade: 'A' },
        { label: '頼りすぎ', grade: 'B' },
        { label: '離職の兆し', grade: 'A' },
      ],
    },
  ],
};
